import React from "react";
import { Document, Page, Text, StyleSheet, View } from "@react-pdf/renderer";
import { SearchResult } from "../../services/search";

interface SearchResultsPDFDocumentProps {
  results: SearchResult[];
  parseMetadata: (raw: any) => any;
  query?: string;
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingHorizontal: 40,
    paddingBottom: 32,
    fontSize: 11,
    fontFamily: "Helvetica",
    color: "#111",
    lineHeight: 1.4,
  },
  title: {
    fontSize: 21,
    marginBottom: 24,
    fontWeight: "bold",
    textAlign: "center",
  },
  headerNote: {
    fontSize: 12,
    textAlign: "center",
    color: "#555",
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: "1px solid #e5e5e5",
  },
  recordRow: {
    flexDirection: "row" as const,
    marginBottom: 16,
    borderBottom: "1px solid #e5e5e5",
    paddingBottom: 10,
  },
  date: {
    width: 120,
    fontSize: 10,
    color: "#03a9f4",
  },
  infoCol: {
    flex: 1,
    paddingLeft: 12,
  },
  recordTitle: {
    fontSize: 13,
    fontWeight: "extrabold",
    marginBottom: 5,
  },
  recordDesc: {
    fontSize: 10,
    marginBottom: 7,
  },
  fieldContainer: {
    flexDirection: "row" as const,
    alignItems: "center",
  },
  fieldKey: {
    fontSize: 10,
    color: "#03a9f4",
  },
  fieldValue: {
    fontSize: 10,
    color: "#666",
    wordBreak: "break-all" as const,
  },
  metadataRow: {
    flexDirection: "row" as const,
    flexWrap: "wrap" as const,
    marginTop: 8,
  },
  chip: {
    backgroundColor: "#e6f5ff",
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 6,
    marginRight: 6,
    marginBottom: 4,
  },
  chipKey: {
    fontSize: 8,
    color: "#03a9f4",
    textTransform: "capitalize" as const,
  },
  chipVal: {
    fontSize: 8,
    color: "#000",
  },
  hash: {
    fontFamily: "Courier",
    fontSize: 10,
    color: "#666",
    wordBreak: "break-all" as const,
    backgroundColor: "#f5f5f5",
    paddingHorizontal: 6,
    borderRadius: 6,
  },
});

export const SearchResultsPDFDocument: React.FC<SearchResultsPDFDocumentProps> = ({
  results,
  parseMetadata,
  query,
}) => {
  // sort oldest -> newest (timestamp ascending)
  const sorted = [...results].sort((a, b) => {
    const tA = parseMetadata(a.metadata_raw).timestamp || 0;
    const tB = parseMetadata(b.metadata_raw).timestamp || 0;
    return new Date(tA).getTime() - new Date(tB).getTime();
  });

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <Text style={styles.title}>Report</Text>
        {query && <Text style={styles.headerNote}>Query: "{query}"</Text>}

        {sorted.map((result, idx) => {
          const metadata = parseMetadata(result.metadata_raw);
          const deviceInfo = metadata.deviceInfo || {};
          const dateStr = new Date(
            metadata.timestamp || metadata.date || result.block_id || Date.now()
          ).toLocaleString();

          const manufacturer = metadata.manufacturer || deviceInfo.manufacturer || "";
          const model = metadata.model || deviceInfo.model || "";
          const identifier =
            metadata.serialNumber ||
            deviceInfo.serialNumber ||
            metadata.identifier ||
            deviceInfo.identifier ||
            "";

          const title = metadata.title || deviceInfo.title || "Untitled Record";

          const descParts = [] as string[];
          if (manufacturer || model) descParts.push(`${manufacturer} ${model}`.trim());
          if (identifier) descParts.push(identifier);

          const description = deviceInfo?.description || descParts.join(" · ");
          const hash = metadata.blockchainData?.hash || "";

          return (
            <View key={idx} style={styles.recordRow}>
              <Text style={styles.date}>{dateStr}</Text>
              <View style={styles.infoCol}>
                <Text style={styles.recordTitle}>{title}</Text>
                {description && <Text style={styles.recordDesc}>{description}</Text>}
                {hash && (
                  <View style={styles.fieldContainer}>
                    {/* <Text style={styles.fieldKey}>Hash: </Text> */}
                    <Text style={styles.hash}>{hash}</Text>
                  </View>
                )}

                {/* metadata chips */}
                <View style={styles.metadataRow}>
                  {Object.entries({
                    Model: model,
                    Serial: identifier,
                    Make: manufacturer,
                    "Hash Alg": metadata.hashAlgorithm || "SHA256",
                    "Record Type": metadata.recordType || "device",
                    Source: metadata.source || "",
                    Block: metadata.blockchainData?.index,
                  })
                    .filter(([, v]) => v !== undefined && v !== null && v !== "")
                    .map(([k, v]) => (
                      <View key={k} style={styles.chip}>
                        <Text style={styles.chipKey}>{k}: </Text>
                        <Text style={styles.chipVal}>{String(v)}</Text>
                      </View>
                    ))}
                </View>
              </View>
            </View>
          );
        })}
      </Page>
    </Document>
  );
};
